// src/components/Rules.js
import React from 'react';
import { Container } from '@mui/material';
import { Link } from 'react-router-dom';

const rules = [
  {
    num: '01',
    icon: '🚗',
    title: 'Eligible Vehicles',
    body: 'Only Tesla Model 3 and Tesla Model Y vehicles are permitted. All variants and model years are welcome — Performance, Long Range, Standard Range.',
    detail: 'Tesla Model 3 · Tesla Model Y',
  },
  {
    num: '02',
    icon: '🛞',
    title: 'No Racing Slicks',
    body: 'All tires must be DOT-approved road legal tires. Pure racing slicks are strictly prohibited. Semi-slicks and high-performance street tires are permitted.',
    detail: 'DOT road legal only',
  },
  {
    num: '03',
    icon: '⚖️',
    title: 'Minimum Weight',
    body: 'The vehicle must not be more than 100 kg lighter than its OEM kerb weight. Weight reduction through interior stripping, battery removal, or structural modification beyond 100 kg will result in disqualification.',
    detail: 'Max −100 kg from OEM weight',
  },
];

const spirit = [
  {
    label: 'Accessibility',
    text: 'The series is designed so that any Model 3 or Model Y owner can participate at their local track with minimal preparation.',
  },
  {
    label: 'Fair Competition',
    text: 'Rules are kept intentionally minimal to reduce barriers, while still ensuring a level competitive baseline across all entrants.',
  },
  {
    label: 'Community First',
    text: 'This is a grassroots series. Sportsmanship, data sharing, and helping fellow competitors improve are central to what we do.',
  },
];

function Rules() {
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pb: 8 }}>

        {/* ── HERO ── */}
        <div style={{
          position: 'relative',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          marginBottom: 48,
          background: 'var(--text-primary)',
          minHeight: 280,
          display: 'flex',
          alignItems: 'flex-end',
          padding: '40px 48px',
        }}>
          <img src="/model3-track-action.jpg" alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 55%', opacity: 0.3 }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.15) 100%)' }} />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: 600 }}>
            <div style={{
              display: 'inline-block', background: 'rgba(25,130,206,0.9)', color: '#fff',
              fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
              padding: '4px 12px', borderRadius: 'var(--radius-pill)', marginBottom: 16,
            }}>
              Sporting Regulations
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)', fontSize: 'clamp(2.4rem, 5vw, 3.6rem)',
              color: '#fff', lineHeight: 1, letterSpacing: '0.03em', marginBottom: 16, fontWeight: 400,
            }}>
              Series Rules
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: '1rem', lineHeight: 1.65, maxWidth: 480 }}>
              Three simple rules. One global leaderboard. Designed to keep competition accessible, fair, and exciting for every Tesla driver.
            </p>
          </div>
        </div>

        {/* ── THE THREE RULES ── */}
        <h2 className="section-heading" style={{ marginTop: 0, textAlign: 'center', marginBottom: 28 }}>The Rules</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 48 }}>
          {rules.map(rule => (
            <div key={rule.num} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '28px 24px',
              boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden',
            }}>
              {/* Watermark number */}
              <div style={{
                position: 'absolute', top: -8, right: 12,
                fontFamily: 'var(--font-display)', fontSize: '5rem',
                color: 'var(--accent-subtle)', lineHeight: 1,
                userSelect: 'none', pointerEvents: 'none',
              }}>
                {rule.num}
              </div>
              <div style={{ fontSize: '2rem', marginBottom: 14 }}>{rule.icon}</div>
              <h3 style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1rem', marginBottom: 10 }}>
                {rule.title}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: 14 }}>
                {rule.body}
              </p>
              {/* Detail pill */}
              <span style={{
                display: 'inline-block', background: 'var(--accent-subtle)', color: 'var(--accent)',
                fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em',
                padding: '4px 12px', borderRadius: 'var(--radius-pill)',
              }}>
                {rule.detail}
              </span>
            </div>
          ))}
        </div>

        {/* ── PLACEHOLDER PHOTO STRIP ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 48 }}>
          {/* Photo 1 — wide */}
          <div style={{
            gridColumn: '1 / -1',
            height: 220,
            borderRadius: 'var(--radius-xl)',
            overflow: 'hidden',
            background: 'var(--text-primary)',
            position: 'relative',
          }}>
            <img src="/model3-track-action.jpg" alt="Model 3 on track"
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.5) 0%, transparent 60%)' }} />
            <div style={{ position: 'absolute', bottom: 20, left: 24 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: '#fff', letterSpacing: '0.04em', lineHeight: 1 }}>
                Any Track. Any Weekend.
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', marginTop: 4 }}>
                Compete at your local circuit
              </div>
            </div>
          </div>
          {/* Photo 2 */}
          <div style={{
            height: 160, borderRadius: 'var(--radius-lg)', overflow: 'hidden',
            background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--border)',
          }}>
            <img src="/model3-track-action.jpg" alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 70%', opacity: 0.55 }} />
          </div>
          {/* Photo 3 */}
          <div style={{
            height: 160, borderRadius: 'var(--radius-lg)', overflow: 'hidden',
            background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--border)',
          }}>
            <img src="/model3-track-action.jpg" alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '30% center', opacity: 0.55 }} />
          </div>
        </div>

        {/* ── SPIRIT OF THE SERIES ── */}
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)', overflow: 'hidden',
          boxShadow: 'var(--shadow-sm)', marginBottom: 48,
        }}>
          {/* Header bar */}
          <div style={{ background: 'var(--text-primary)', padding: '20px 32px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', letterSpacing: '0.04em', color: '#fff', fontWeight: 400 }}>
              Spirit of the Series
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', marginTop: 4 }}>
              Rules on paper, ethos in practice
            </div>
          </div>
          {spirit.map((item, i, arr) => (
            <div key={item.label} style={{
              display: 'flex', gap: 20, padding: '20px 32px',
              borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: '1.1rem', letterSpacing: '0.04em',
                color: 'var(--accent)', minWidth: 130, paddingTop: 2, flexShrink: 0,
              }}>
                {item.label.toUpperCase()}
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                {item.text}
              </p>
            </div>
          ))}
        </div>

        {/* ── CTA ── */}
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)', padding: '36px 48px',
          textAlign: 'center', boxShadow: 'var(--shadow-sm)',
        }}>
          <h2 className="section-heading" style={{ marginTop: 0, marginBottom: 10, textAlign: 'center' }}>
            Ready to Race?
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 480, margin: '0 auto 24px' }}>
            Head to the leaderboard, submit your first lap, and join the only global time attack series built for Tesla drivers.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/signup" style={{
              background: 'var(--accent)', color: '#fff', padding: '12px 28px',
              borderRadius: 'var(--radius-md)', fontWeight: 700, fontSize: '0.9rem',
              textDecoration: 'none', display: 'inline-block',
            }}>
              Create Your Profile
            </Link>
            <Link to="/about" style={{
              background: 'transparent', color: 'var(--accent)', padding: '12px 28px',
              borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: '0.9rem',
              textDecoration: 'none', display: 'inline-block', border: '1px solid var(--accent)',
            }}>
              Learn More
            </Link>
          </div>
        </div>

      </Container>
    </div>
  );
}

export default Rules;
